/**
 * Normalises `POST /vehicle` on the property deployment.
 *
 * The response is the motor vehicle record with the vehicle fields left null —
 * make, model, num_plate, vin_reading, odo_meter_reading are all absent or "-"
 * — and the property in `property_data`: one entry per photograph, each with
 * its own coordinates.
 *
 * Only two things are actually wanted from it: the images and where they were
 * taken. Everything else here is capture metadata the response happens to
 * carry, kept because it is free and answers "can I trust these photos".
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

/** "bathroom_and_utility" -> "Bathroom and utility" */
export const humanise = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  const words = value.trim().split(/[_\-\s]+/);
  return words
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(" ");
};

/**
 * Sides in the order someone walks a property: the outside, then the rooms,
 * then whatever else was photographed. The API's own order is upload order.
 */
const SIDE_ORDER = ["exterior", "interior", "additional"];
const SIDE_LABELS = {
  exterior: "Exterior",
  interior: "Interior",
  additional: "Additional",
};

const sideRank = (side) => {
  const index = SIDE_ORDER.indexOf(side);
  return index === -1 ? SIDE_ORDER.length : index;
};

/** A coordinate is only usable if it parses and is not the API's "-" or "0". */
const coordinate = (value) => {
  if (value === null || value === undefined || value === "-") return null;
  const number = Number(value);
  return Number.isFinite(number) && number !== 0 ? number : null;
};

export const toPropertyDetail = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const photos = asArray(raw.property_data)
    .filter((entry) => entry?.picture)
    .map((entry, index) => ({
      id: `${entry.name || "photo"}-${index}`,
      picture: entry.picture,
      side: entry.side || "additional",
      name: entry.name || "",
      label: humanise(entry.name) || "Photograph",
      createdAt: entry.created_at || null,
      lat: coordinate(entry.lat),
      // CustomMap reads `longitude`; the API calls it `long`. Renamed here so
      // the map needs no property-specific branch.
      longitude: coordinate(entry.long),
    }));

  // Grouped for display, and kept flat for the map, which clusters by
  // coordinate rather than by side.
  const groups = [...new Set(photos.map((photo) => photo.side))]
    .sort((a, b) => sideRank(a) - sideRank(b) || a.localeCompare(b))
    .map((side) => ({
      side,
      label: SIDE_LABELS[side] || humanise(side),
      photos: photos.filter((photo) => photo.side === side),
    }));

  const located = photos.filter(
    (photo) => photo.lat !== null && photo.longitude !== null,
  );

  return {
    refNum: raw.ref_num || null,
    sourceName: raw.source_name || null,
    address: raw.address || null,
    claimState: raw.claim_state ?? null,
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
    userId: raw.user_id || null,

    photos,
    groups,
    located,

    /**
     * Where the property is: the coordinate most photographs share.
     *
     * Not an average. Handset GPS drifts a few metres between shots, and
     * averaging would put the marker at a point no photograph was taken from —
     * whereas the modal coordinate is a real one.
     */
    location: (() => {
      if (!located.length) return null;
      const counts = new Map();
      located.forEach((photo) => {
        const key = `${photo.lat},${photo.longitude}`;
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      const [best] = [...counts.entries()].sort((a, b) => b[1] - a[1]);
      const [lat, longitude] = best[0].split(",").map(Number);
      return { lat, longitude, photoCount: best[1] };
    })(),

    counts: {
      total: Number(raw.total_images ?? raw.image_count) || photos.length,
      exterior: Number(raw.exterior_count) || 0,
      interior: Number(raw.interior_count) || 0,
      additional: Number(raw.additional_count) || 0,
      located: located.length,
    },

    // Free with the response, and it is what says whether the photographs can
    // be trusted: who shot them, on what, how fast, and how long it took.
    capture: {
      inspectionMode: raw.inspection_mode || null,
      device: raw.device_make_model || null,
      os: raw.os_summary || null,
      timeTaken: raw.time_taken_to_upload || null,
      userRating: raw.user_rating ?? null,
      internet: {
        min: raw.min_internet_speed || null,
        max: raw.max_internet_speed || null,
        note: raw.low_internet_speed || null,
      },
    },
  };
};

export default toPropertyDetail;
